import React, { useEffect, useState, useRef, useCallback } from "react";
import ApiService from "../../services/api-service";
import { CircularProgress, Box, Typography } from "@mui/material";
import FeedCard from "../../components/Feed/FeedCard";
import FeedMediaCard from "../../components/Feed/FeedMediaCard";
import AddMusicToPlaylist from "../../components/AddMusicToPlaylist";
import "./Feed.scss";

const isMusic = (item) => item && (item.contentType === "music" || (!item.contentType && item.sampleData));

const Feed = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPlayingIdx, setCurrentPlayingIdx] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [userPausedIdx, setUserPausedIdx] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showPlaylist, setShowPlaylist] = useState(false);
    const [userSaves, setUserSaves] = useState([]); // ← Add this line!


  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [cursor, setCursor] = useState(new Date().toISOString()); // Start from now

  const cardRefs = useRef([]);
  const lastActiveIdx = useRef(null);

  // Stable per-index ref setter so FeedCard's cardRef prop doesn't change every
  // render (which would defeat React.memo on FeedCard).
  const setCardRef = useCallback((idx, el) => {
    cardRefs.current[idx] = el;
  }, []);

  // Refs so the scroll handler always reads current values without re-binding.
  const hasMoreRef = useRef(hasMore);
  const fetchingMoreRef = useRef(fetchingMore);
  const cursorRef = useRef(cursor);

  // Keep refs in sync with state so the scroll handler reads current values.
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { fetchingMoreRef.current = fetchingMore; }, [fetchingMore]);
  useEffect(() => { cursorRef.current = cursor; }, [cursor]);

   // Fetch user's saved songs
  const fetchUserSaves = async () => {
    try {
      const response = await ApiService.GET_SAVES('music');
      const savedSongIds = response.data || [];
      setUserSaves(savedSongIds);
      console.log('[Feed] User saves loaded:', savedSongIds.length);
    } catch (error) {
      console.error('[Feed] Error fetching user saves:', error);
    }
  };

   // Handle save toggle. Stable identity (functional state update + empty deps)
  // so it doesn't force memoized cards to re-render every parent render.
  const handleSaveToggle = useCallback((itemId, isSaved) => {
    setUserSaves(prev =>
      isSaved ? [...prev, itemId] : prev.filter(id => id !== itemId)
    );
  }, []);


  // Fetch feed data (with cursor-based pagination)
const fetchFeeds = async (append = false, cursorValue = null) => {
  try {
    if (append) setFetchingMore(true);
    else setLoading(true);

    // Use the provided cursor or the current state
    const cursorToUse = cursorValue || cursor;
    const params = {
      direction: "older",
      cursor: cursorToUse,
    };

    console.log(`[Feed] Fetching feeds. append=${append}, cursor=${cursorToUse}`);
    const response = await ApiService.FETCH_UNIFIED_FEED(params);
    const { items: newItems, nextCursor, hasMore: moreAvailable } = response.result || {};

    console.log(`[Feed] Received ${newItems?.length || 0} items from API`);
    if (newItems && newItems.length > 0) {
      setItems(prev => {
        // Combine previous and new items if appending, else just use new items
        const combined = append ? [...prev, ...newItems] : newItems;
        // De-duplicate by _id (cursor boundaries can re-surface an item)
        const seen = new Set();
        return combined.filter(it => {
          const key = it._id || it.filePath;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });
      setHasMore(Boolean(moreAvailable));

      // Advance cursor using the server-provided nextCursor (post time of the
      // last merged item across all content types).
      if (nextCursor) {
        setCursor(nextCursor);
      }
    } else {
      setHasMore(false);
      console.log("[Feed] No more items to load.");
    }
  } catch (err) {
    setError("Failed to load feeds.");
    console.error("[Feed] Error fetching feeds:", err);
  } finally {
    setLoading(false);
    setFetchingMore(false);
  }
};

  // Initial fetch
  useEffect(() => {
    // Stop any global player when entering feed
    const stopGlobalPlayer = () => {
      // Dispatch a global event to stop all players
      window.dispatchEvent(new CustomEvent('stop-all-players', { 
        detail: { source: 'feed' } 
      }));
      
      // Check if there's a global audio player playing
      const allAudioElements = document.querySelectorAll('audio');
      allAudioElements.forEach(audio => {
        if (!audio.paused) {
          console.log('[Feed] Stopping global audio player');
          audio.pause();
          audio.currentTime = 0;
        }
      });
      
      // Also stop any WaveSurfer instances that might be playing
      const wavesurferElements = document.querySelectorAll('.wavesurfer-player');
      wavesurferElements.forEach(element => {
        // Dispatch a custom event to stop wavesurfer
        element.dispatchEvent(new CustomEvent('stop-playback'));
      });
    };
    
    stopGlobalPlayer();
    setCursor(new Date().toISOString());
    fetchFeeds(false, new Date().toISOString());
    fetchUserSaves(); // Fetch user saves on mount

    // Cleanup function when leaving the feed
    return () => {
      console.log('[Feed] Component unmounting, stopping all feed audio');
      // Stop all audio elements in the feed
      const allAudioElements = document.querySelectorAll('audio');
      allAudioElements.forEach(audio => {
        if (!audio.paused) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    };

    // eslint-disable-next-line
  }, []);

  // Active-card detection + infinite scroll.
  // The active (playing/highlighted) card is the one whose center is closest to
  // the viewport center — not the one with the largest visible fraction. With
  // cards taller than the viewport (photos/videos), a ratio-based pick would
  // never select the centered card and focus would stick to a shorter top or
  // bottom card. Center-distance works regardless of card height.
  useEffect(() => {
    const scrollContainer = document.querySelector('.feed-scroll-outer');
    if (!scrollContainer) return;

    const updateActiveCard = () => {
      const cRect = scrollContainer.getBoundingClientRect();
      const centerY = cRect.top + cRect.height / 2;

      let bestIdx = null;
      let bestDist = Infinity;
      cardRefs.current.forEach((ref, idx) => {
        if (!ref) return;
        const r = ref.getBoundingClientRect();
        // Ignore cards fully outside the viewport.
        if (r.bottom < cRect.top || r.top > cRect.bottom) return;
        const dist = Math.abs(r.top + r.height / 2 - centerY);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = idx;
        }
      });

      if (bestIdx === null || bestIdx === lastActiveIdx.current) return;
      lastActiveIdx.current = bestIdx;
      setCurrentPlayingIdx(bestIdx);
      setIsPaused(false);
      setUserPausedIdx(null);

      if (bestIdx >= items.length - 3 && hasMoreRef.current && !fetchingMoreRef.current) {
        fetchFeeds(true, cursorRef.current);
      }
    };

    let raf = null;
    const onScroll = () => {
      // Near the bottom → load more.
      if (
        hasMoreRef.current &&
        !fetchingMoreRef.current &&
        scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 100
      ) {
        fetchFeeds(true, cursorRef.current);
      }
      // Recompute the centered card (throttled to a frame).
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateActiveCard);
    };

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    // Pick an initial active card once cards are laid out.
    raf = requestAnimationFrame(updateActiveCard);

    return () => {
      scrollContainer.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line
  }, [items]);

  // Handle play/pause from user interaction. Stable identity (functional state
  // updates + empty deps) so per-card onPlay closures stay referentially stable
  // and React.memo on FeedCard stays effective.
  const handleCardPlay = useCallback((idx) => {
    setCurrentPlayingIdx((prevIdx) => {
      if (prevIdx === idx) {
        // Same card: toggle pause state.
        setIsPaused((prev) => {
          setUserPausedIdx(!prev ? idx : null);
          return !prev;
        });
        return prevIdx;
      }
      // Different card: switch active card and resume playback.
      setIsPaused(false);
      setUserPausedIdx(null);
      lastActiveIdx.current = idx;
      return idx;
    });
    console.log(`[Feed] handleCardPlay: idx=${idx}`);
  }, []);

  if (loading && items.length === 0) {
    return (
      <Box className="feed-loading" sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="feed-error" sx={{ textAlign: "center", padding: "20px" }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="feed-scroll-outer"
      sx={{
        backgroundColor: "var(--background-color, #000)",
        minHeight: "100vh",
        overflowY: "auto",
        position: "relative",
      }}
    >
      <Box
        className="feed-scroll-inner"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {showPlaylist && (
          <AddMusicToPlaylist
            musicId={selectedAlbum}
            open={showPlaylist}
            handleClose={() => setShowPlaylist(false)}
          />
        )}
        {items.map((item, idx) =>
          isMusic(item) ? (
            <FeedCard
              key={item._id}
              item={item}
              index={idx}
              isPlaying={currentPlayingIdx === idx && !isPaused}
              isPaused={isPaused && currentPlayingIdx === idx}
              onPlay={handleCardPlay} // Stable ref; FeedCard invokes with its index
              setCardRef={setCardRef} // Stable per-index ref setter
              data-idx={idx}
              setSelectedAlbum={setSelectedAlbum}
              setShowPlaylist={setShowPlaylist}
              userSaves={userSaves} // Pass userSaves
              onSaveToggle={handleSaveToggle} // Pass the handler
            />
          ) : (
            <FeedMediaCard
              key={item._id}
              item={item}
              isActive={currentPlayingIdx === idx && !isPaused}
              onPlay={() => handleCardPlay(idx)}
              cardRef={el => (cardRefs.current[idx] = el)}
              data-idx={idx}
            />
          )
        )}
        {fetchingMore && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!hasMore && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 2 }}>
            No more posts to load.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Feed;